import React, {useEffect, useMemo, useRef, useState} from 'react'
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalOverlay,
    SimpleGrid,
    Text,
    useDisclosure,
    useStyleConfig,
    Flex,
    useBreakpointValue,
    ModalCloseButton,
} from '@chakra-ui/react'
import {CheckIcon, CloseIcon, CopyLinkIcon} from '../../../components/icons'

import {FormattedMessage} from 'react-intl'
import {useCompare} from '../../../context/compare'
import {remCalc} from '../../../theme/_utils'
import CompareTables from './compare-tables'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../../custom-api/layers/default'
import CompareTiles from './compare-tiles'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {useArrayOfSpecifications} from '../../../utils/specifications-utils'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import PropTypes from 'prop-types'

const getProductIdsFromCompareItems = (compareItems) => {
    return compareItems.map((item) => item.productId)
}

const getProductKey = (product) => product?.productId || product?.id

const createCompareSlots = (items, size) =>
    Array.from({length: size}, (_, index) => items[index] || null)

const getProductsInCompareOrder = (fetchedProducts, slotItems) => {
    const productsById = new Map(
        (fetchedProducts || []).map((product) => [getProductKey(product), product]),
    )

    // Keep full single-product responses available while the bulk query catches up.
    return (slotItems || []).map((item) => productsById.get(getProductKey(item)) || item)
}

const getLocalizedCompareName = (product) =>
    product?.c_variantIdentifier ||
    product?.name ||
    product?.productName ||
    product?.c_defaultName ||
    product?.productId ||
    product?.id ||
    ''

const slugifyCompareName = (value = '') =>
    String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

const CompareModal = ({categoryId, controlsDisplay, showControlsOnMobile}) => {
    const [hideIdenticalRows, setHideIdenticalRows] = useState(false)
    const [isLinkCopied, setIsLinkCopied] = useState(false)
    const copyFeedbackTimeout = useRef(null)
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {addLayer} = useGoogleTagManager()
    const maxCompareItems = useBreakpointValue({base: 2, md: 3}) || 3

    useEffect(() => () => clearTimeout(copyFeedbackTimeout.current), [])

    const {
        clearItems,
        updateItem,
        state: {compareItems},
    } = useCompare()

    const styles = useStyleConfig('ProductComparePlp')
    const {locale, buildUrl} = useMultiSite()
    const {currency} = useCurrency()

    const visibleItems = compareItems.slice(0, maxCompareItems)
    const compareSlots = createCompareSlots(visibleItems, maxCompareItems)
    const compareColumns = compareSlots.length
    const compareGridMaxWidth = `${remCalc(compareColumns * 400 + (compareColumns - 1) * 40)}`

    const productIds = getProductIdsFromCompareItems(visibleItems)

    const {data: productIdsData} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                locale: locale.id,
                currency,
                c_includeTechSpecs: true,
                expand: ['availability', 'images', 'prices', 'promotions'],
            },
            headers: {
                'x-debug': 'compare-table-index',
            },
        },
        {
            enabled: productIds.length > 0,
            keepPreviousData: true,
        },
    )

    const orderedProducts = getProductsInCompareOrder(productIdsData?.data, visibleItems)
    const fetchedProductsById = useMemo(
        () =>
            new Map(
                (productIdsData?.data || []).map((product) => [getProductKey(product), product]),
            ),
        [productIdsData],
    )
    const hydratedProductIdsByIndex = useRef({})

    useEffect(() => {
        if (compareItems.length === 0) {
            hydratedProductIdsByIndex.current = {}
        }
    }, [compareItems.length])

    useEffect(() => {
        if (compareItems.length === 0 && isOpen) {
            onClose()
        }
    }, [compareItems.length, isOpen, onClose])

    useEffect(() => {
        visibleItems.forEach((item, index) => {
            const itemKey = getProductKey(item)
            if (!itemKey) return

            const fetchedProduct = fetchedProductsById.get(itemKey)
            if (!fetchedProduct) return

            if (hydratedProductIdsByIndex.current[index] === itemKey) return

            updateItem(index, {
                ...fetchedProduct,
                productId:
                    fetchedProduct?.productId || fetchedProduct?.id || item?.productId || item?.id,
                id: fetchedProduct?.id || fetchedProduct?.productId || item?.id || item?.productId,
            })

            hydratedProductIdsByIndex.current[index] = itemKey
        })
    }, [visibleItems, fetchedProductsById, updateItem])

    const accordionData = useArrayOfSpecifications(orderedProducts)
    const numOfProducts = compareColumns
    const visibleColumns = compareSlots.map(Boolean)

    const handleShareLink = async () => {
        const shareableItems = compareItems.slice(0, maxCompareItems)
        const shareableProductIds = getProductIdsFromCompareItems(shareableItems)

        if (shareableProductIds.length === 0) return

        const compareSlug = (orderedProducts.length > 0 ? orderedProducts : shareableItems)
            .map((product) => slugifyCompareName(getLocalizedCompareName(product)))
            .filter(Boolean)
            .join('-vs-')

        const queryParams = new URLSearchParams()
        shareableProductIds.forEach((productId, index) => {
            queryParams.set(`sku${index + 1}`, productId)
        })
        if (categoryId) {
            queryParams.set('categoryId', categoryId)
        }

        const path = `/compare/${compareSlug || 'products'}?${queryParams.toString()}`
        const relativeUrl = buildUrl(path)
        const fullUrl = `${window.location.origin}${relativeUrl}`

        try {
            await navigator.clipboard.writeText(fullUrl)
        } catch {
            return
        }

        clearTimeout(copyFeedbackTimeout.current)
        setIsLinkCopied(true)
        copyFeedbackTimeout.current = setTimeout(() => setIsLinkCopied(false), 4000)
    }

    const handleCompareButtonClick = async () => {
        onOpen()
        await addLayer(
            basic_gtm_event.bind(this, {
                event: 'comparisonTool',
                eventCategory: 'ecommerce',
                eventAction: 'comparisonToolListing',
                eventLabel: 'tabLoaded',
            }),
        )
    }

    const isMobile = useBreakpointValue({base: true, md: false})

    return (
        <>
            {(!isMobile || showControlsOnMobile) && (
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    display={controlsDisplay}
                    w="100%"
                    gap="xxxsmall.base"
                >
                    <Button
                        onClick={() => clearItems()}
                        variant="ghost"
                        color="foreground.neutral.inverse"
                        size="small"
                        _hover={{background: 'image.background'}}
                    >
                        <Text color="foreground.neutra.default">
                            <FormattedMessage
                                id="product_list.compare_button.clear_all"
                                defaultMessage="Remove items"
                            />
                        </Text>
                    </Button>
                    <Button
                        isDisabled={compareItems.length === 1}
                        onClick={handleCompareButtonClick}
                        variant="filter-button"
                        size="small"
                        textStyle="body-xsmall"
                        fontWeight="medium"
                        color="foreground.neutral.default"
                        borderRadius="large"
                    >
                        <FormattedMessage
                            id="product_list.compare_button.compare"
                            defaultMessage="Compare"
                        />
                    </Button>
                </Flex>
            )}
            <Modal onClose={onClose} size="full" isOpen={isOpen}>
                <ModalOverlay background="background.base.default" />
                <ModalContent w="100vw" maxW="100vw" m={0} background="background.base.default">
                    <ModalBody
                        {...styles.modal}
                        layerStyle="page"
                        paddingX={{
                            base: 'unset',
                            sm: 'unset',
                            md: 'auto',
                        }}
                    >
                        <Flex
                            position="absolute"
                            top={remCalc(24)}
                            right={remCalc(24)}
                            alignItems="center"
                            justifyContent="flex-end"
                            gap={{base: remCalc(8), md: remCalc(12)}}
                            zIndex="999"
                        >
                            <Button
                                variant="icon-button-outline"
                                size="small"
                                leftIcon={
                                    isLinkCopied ? (
                                        <CheckIcon
                                            width={remCalc(10)}
                                            height={remCalc(10)}
                                            color="foreground.neutral.default"
                                        />
                                    ) : (
                                        <CopyLinkIcon
                                            boxSize="iconsSize.small"
                                            color="foreground.neutral.default"
                                        />
                                    )
                                }
                                onClick={handleShareLink}
                            >
                                {isLinkCopied ? (
                                    <FormattedMessage
                                        id="compare-modal.copy-link.copied"
                                        defaultMessage="Copied"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="compare-modal.copy-link.text"
                                        defaultMessage="Copy link"
                                    />
                                )}
                            </Button>

                            <Flex>
                                <ModalCloseButton position="unset">
                                    <CloseIcon {...styles.modalCloseIcon} />
                                </ModalCloseButton>
                            </Flex>
                        </Flex>
                        <SimpleGrid {...styles.modalBody} maxW={compareGridMaxWidth} mx="auto">
                            <CompareTiles
                                productsWithCompareData={productIdsData?.data || []}
                                hideIdenticalRows={hideIdenticalRows}
                                onHideIdenticalRowsChange={setHideIdenticalRows}
                                categoryId={categoryId}
                                locale={locale}
                            />
                            <CompareTables
                                accordionData={accordionData}
                                numOfProducts={numOfProducts}
                                visibleColumns={visibleColumns}
                                gridGap={styles.modalProductTilesGrid?.gap}
                                hideIdenticalRows={hideIdenticalRows}
                            />
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter />
                </ModalContent>
            </Modal>
        </>
    )
}

CompareModal.propTypes = {
    categoryId: PropTypes.string,
    controlsDisplay: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    showControlsOnMobile: PropTypes.bool,
}

CompareModal.defaultProps = {
    controlsDisplay: 'flex',
    showControlsOnMobile: false,
}

export default CompareModal
